import { IconDownload } from '@iwoca/icons';
import { Button, Card } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { FileUpload } from 'components/Registration/FileUpload/FileUpload';
import { useFormContext } from 'react-hook-form';
import {
  TDocTypeDE,
  TOnboardingFormValuesDE,
  TUploadStep,
} from '../onboardingFormDE.types';
import styles from './OnboardingDocumentsDE.module.css';

// Signed partner questionnaire template — same asset used on the DE no-products
// page (src/components/ProductItems/NoProducts.tsx).
const PARTNER_QUESTIONNAIRE_PDF_URL =
  'https://static.iwoca.com/pdf/de/Partner-Fragebogen-v2.pdf';

type TProps = {
  introducerType: 'broker' | 'partner';
  disabled?: boolean;
};

export const OnboardingDocumentsDE = ({
  introducerType,
  disabled = false,
}: TProps) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<TOnboardingFormValuesDE>();

  const uploadedLabel = t`Document uploaded`;

  const allUploadSteps: TUploadStep[] = [
    {
      docType: 'broker_questionnaire',
      field: 'brokerQuestionnaireFile',
      label: t`Upload the signed questionnaire`,
    },
    {
      docType: 'hr_trade_certificate',
      field: 'hrTradeCertificateFile',
      label: t`Current commercial register extract / trade licence`,
      instruction: t`Commercial register extract or trade licence`,
    },
    {
      docType: 'license_34c',
      field: 'license34cFile',
      label: t`§34c GewO (loan brokering)`,
      instruction: t`Your official trade permit under §34c(1) sentence 1 no. 2 GewO`,
      brokerOnly: true,
    },
    {
      docType: 'certified_id',
      field: 'certifiedIdFile',
      label: t`ID copies (managing directors and beneficial owners)`,
      instruction: t`Your official photo IDs (passport or ID card)`,
    },
  ];

  const uploadSteps = allUploadSteps.filter(
    (step) => introducerType === 'broker' || !step.brokerOnly,
  );

  return (
    <>
      <h2>
        <Trans>Documents</Trans>
      </h2>
      <Card
        className={clsx(styles.documentsContainer, disabled && styles.disabled)}
      >
        <div className={styles.step}>
          <span className={styles.badge}>1</span>
          <div className={styles.stepContent}>
            <p className={styles.stepLabel}>
              <Trans>Partner questionnaire</Trans>
            </p>
            <p className={styles.instruction}>
              <Trans>
                Download the questionnaire, sign it, and then upload the signed
                copy.
              </Trans>
            </p>
            <div className={styles.downloadRow}>
              <span className={styles.downloadRowLabel}>
                <Trans>Download partner questionnaire</Trans>
              </span>
              <Button
                variant="secondary-alt"
                size="small"
                href={PARTNER_QUESTIONNAIRE_PDF_URL}
                target="_blank"
                rel="noreferrer"
                disabled={disabled}
              >
                <IconDownload width={16} height={16} />
                <Trans>Download PDF</Trans>
              </Button>
            </div>
          </div>
        </div>

        {uploadSteps.map(({ docType, field, label, instruction }, index) => (
          <div className={styles.step} key={field}>
            <span className={styles.badge}>{index + 2}</span>
            <div className={styles.stepContent}>
              <FileUpload<TDocTypeDE>
                multiple
                docType={docType}
                label={label}
                instruction={instruction}
                uploadedLabel={uploadedLabel}
                selectedFiles={watch(field)}
                errorMessage={errors[field]?.message}
                disabled={disabled}
                onChange={(files) =>
                  setValue(field, files, { shouldValidate: true })
                }
              />
            </div>
          </div>
        ))}
      </Card>
    </>
  );
};
